/**
 * GravityMenu — live slider popover for the Simulate Gravity operator.
 *
 * Two modes share the menu:
 *   1. Default endpoint bake — runs the moment the menu opens and on every
 *      parameter change. Off-thread (physics worker), short, indeterminate
 *      ProgressBar. Time slider is hidden because there's no trajectory.
 *   2. Opt-in time-range bake — a "Bake Time Range" disclosure lets the
 *      user pick start / end substeps, then a Bake button captures a
 *      scrubbable trajectory. Determinate ProgressBar; the Time slider
 *      activates once a bake completes.
 *
 * In-flight bakes are cancelled cooperatively when settings change so the
 * menu stays responsive even on heavy cloth meshes. Closing the menu
 * discards both bake caches.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { selectionToLeafObjectIds } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import type { GravityBakeResult } from "../app/storeTypes.ts";
import { verticesDiagonal } from "../modeling/objectBounds.ts";
import { DEFAULT_SETTLE_PARAMS } from "../modeling/physicsSim.ts";
import type { CurveData } from "../modeling/types.ts";
import { EDGE_WIRE } from "../modeling/types.ts";
import {
  toVertexGroupOptions,
  type VertexGroupOption,
} from "../modeling/vertexGroupOptions.ts";
import { Button } from "./Button.tsx";
import { FloatingCommandMenu } from "./FloatingCommandMenu.tsx";
import { useDeferredUndo } from "./hooks/useDeferredUndo.ts";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";
import { ProgressBar } from "./ProgressBar.tsx";
import { Slider } from "./Slider.tsx";
import { FoldoutSection, SelectSearch } from "./shared.tsx";
import {
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT_DIM,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
} from "./tokens.ts";

/** Baseline snapshot for one target object — whichever fields apply. */
interface GravityBaseline {
  objectId: string;
  curveData?: CurveData;
  vertices?: Float32Array;
  /** True when this baseline's target runs through the cloth dispatch
   *  (faced mesh with no wire edges). Drives whether the Stiffness and
   *  Self-Collision sliders apply to this object. */
  isCloth: boolean;
  /** True when the store supports baking for this target kind — i.e.
   *  it's a mesh (cloth or wire chain). Curves are excluded; they run
   *  through the non-baked path and don't participate in time-range
   *  scrubbing. */
  canBake: boolean;
  /** Local-space bbox diagonal — used to scale the Self-Collision
   *  slider's percentage into absolute world units. Zero for non-cloth. */
  meshSize: number;
}

/** Elasticity slider maps 0..100 linearly to compliance 0..MAX_COMPLIANCE. */
const MAX_COMPLIANCE = 1e-3;

/** Tautness slider maps 0..100 linearly to rest-length multiplier 1..0.5. */
const MIN_TAUTNESS_PARAM = 0.5;

/** Stiffness slider (cloth only) maps 0..100 logarithmically to bending
 *  compliance 1e-2..1e-6. */
function stiffnessToBendingCompliance(pct: number): number {
  const clamped = Math.max(0, Math.min(100, pct));
  return 10 ** (-2 - (clamped / 100) * 4);
}

/** Self-collision slider (cloth only) — percentage of mesh diagonal. */
const MAX_SELF_COLLISION_FRACTION = 0.05;

function selfCollisionPctToThickness(pct: number, meshSize: number): number {
  const clamped = Math.max(0, Math.min(100, pct));
  return (clamped / 100) * MAX_SELF_COLLISION_FRACTION * meshSize;
}

function elasticityToCompliance(pct: number): number {
  return (Math.max(0, Math.min(100, pct)) / 100) * MAX_COMPLIANCE;
}

function tautnessToParam(pct: number): number {
  const clamped = Math.max(0, Math.min(100, pct));
  return 1 - (clamped / 100) * (1 - MIN_TAUTNESS_PARAM);
}

/** Bake-recorder stride: snapshots every N substeps. Tuned for ~50–80
 *  frames per typical settle. */
const BAKE_STRIDE = 5;

/** Solver's default substep ceiling — also the default end of the
 *  trajectory bake range. Sourced from the canonical defaults so it
 *  tracks any future tuning of the solver. */
const DEFAULT_END_SUBSTEP = DEFAULT_SETTLE_PARAMS.maxSubsteps;

/** One-click endpoint-bake budget. Tuned empirically: most ropes and
 *  typical cloth drapes look "settled enough" within ~120 substeps.
 *  Remaining residual jitter is invisible at interactive rates and
 *  the Bake Time Range workflow is available for anyone who wants to
 *  see the full settle evolution. Keeping this well below the solver's
 *  400-substep cap is the whole point — the default click-and-done
 *  path should feel snappy, not thorough. The store separately widens
 *  the progress-callback stride for endpoint bakes so the postMessage
 *  traffic doesn't tax the short solves. */
const ENDPOINT_BAKE_MAX_SUBSTEPS = 120;

/** Sample frame `f` (fractional 0..frameCount-1) of a cached bake into
 *  the supplied output buffer. */
function sampleBakeFrame(
  bake: GravityBakeResult,
  f: number,
  out: Float32Array,
): void {
  const stride = bake.vertexCount * 3;
  if (bake.frameCount <= 1) {
    out.set(bake.frames.subarray(0, stride));
    return;
  }
  const maxF = bake.frameCount - 1;
  const clamped = Math.max(0, Math.min(maxF, f));
  const i0 = Math.floor(clamped);
  const i1 = Math.min(maxF, i0 + 1);
  const t = clamped - i0;
  const off0 = i0 * stride;
  const off1 = i1 * stride;
  if (t === 0 || i0 === i1) {
    out.set(bake.frames.subarray(off0, off0 + stride));
    return;
  }
  const oneMinusT = 1 - t;
  for (let i = 0; i < stride; i++) {
    out[i] = bake.frames[off0 + i] * oneMinusT + bake.frames[off1 + i] * t;
  }
}

type BakePhase = "endpoint" | "trajectory" | null;

export function GravityMenu() {
  const menu = useOperatorMenu("gravity");
  const undo = useDeferredUndo();

  const [elasticity, setElasticity] = useState(0);
  const [tautness, setTautness] = useState(0);
  const [stiffness, setStiffness] = useState(50);
  const [selfCollision, setSelfCollision] = useState(0);
  // `time` is a 0..100 % scrub. Only meaningful after a trajectory bake
  // completes — the Time slider is hidden until then.
  const [time, setTime] = useState(100);
  const [pinGroupId, setPinGroupId] = useState<string | null>(null);
  const [hasClothTarget, setHasClothTarget] = useState(false);
  const [pinGroupOptions, setPinGroupOptions] = useState<VertexGroupOption[]>(
    [],
  );
  const [pinGroupUnavailableReason, setPinGroupUnavailableReason] = useState<
    string | null
  >(null);

  // Trajectory-range disclosure state.
  const [trajectoryStart, setTrajectoryStart] = useState(0);
  const [trajectoryEnd, setTrajectoryEnd] = useState(DEFAULT_END_SUBSTEP);
  /** True once a trajectory bake completes for at least one target. The
   *  Time slider is gated on this — there's nothing to scrub otherwise. */
  const [hasTrajectoryBake, setHasTrajectoryBake] = useState(false);

  // Bake feedback shown via ProgressBar.
  const [bakePhase, setBakePhase] = useState<BakePhase>(null);
  /** `undefined` keeps the bar in indeterminate (shimmer) mode — used
   *  for the endpoint bake where total substeps is unknown ahead of time. */
  const [bakeProgress, setBakeProgress] = useState<number | undefined>(
    undefined,
  );

  // Refs for in-flight bake bookkeeping.
  const baselinesRef = useRef<GravityBaseline[]>([]);
  const bakesRef = useRef<Map<string, GravityBakeResult>>(new Map());
  /** Single AbortController for whatever bake is currently running. New
   *  bakes always abort the prior one before kicking off — matches the
   *  inking worker's cancel-restart pattern. */
  const abortRef = useRef<AbortController | null>(null);
  /** Stamps each bake invocation so out-of-order completions (e.g. the
   *  prior bake finishes after we've started a new one) are ignored. */
  const bakeSeqRef = useRef(0);

  const cancelInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelInFlight();
    setElasticity(0);
    setTautness(0);
    setStiffness(50);
    setSelfCollision(0);
    setTime(100);
    setPinGroupId(null);
    setPinGroupOptions([]);
    setPinGroupUnavailableReason(null);
    setHasClothTarget(false);
    setTrajectoryStart(0);
    setTrajectoryEnd(DEFAULT_END_SUBSTEP);
    setHasTrajectoryBake(false);
    setBakePhase(null);
    setBakeProgress(undefined);
    baselinesRef.current = [];
    bakesRef.current.clear();
    undo.reset();
    menu.close();
  }, [menu, undo, cancelInFlight]);

  /**
   * Endpoint bake — the default. Captures one final-rest pose per target
   * with no trajectory recording. Fast enough that the indeterminate bar
   * usually flicks past in a few hundred ms; running off-thread keeps the
   * viewport responsive for heavy cloths regardless.
   */
  const rebakeEndpoint = useCallback(
    async (
      nextElasticity: number,
      nextTautness: number,
      nextStiffness: number,
      nextSelfCollision: number,
      nextPinGroupId: string | null,
    ) => {
      cancelInFlight();
      const controller = new AbortController();
      abortRef.current = controller;
      const seq = ++bakeSeqRef.current;

      setElasticity(nextElasticity);
      setTautness(nextTautness);
      setStiffness(nextStiffness);
      setSelfCollision(nextSelfCollision);
      setPinGroupId(nextPinGroupId);
      // Clear trajectory cache: the physics parameters changed so any
      // previous time-range bake is now stale. Time slider re-hides.
      bakesRef.current.clear();
      setHasTrajectoryBake(false);
      setTime(100);
      setBakePhase("endpoint");
      setBakeProgress(undefined);

      const s = useStore.getState();
      const compliance = elasticityToCompliance(nextElasticity);
      const tautnessParam = tautnessToParam(nextTautness);
      const bendingCompliance = stiffnessToBendingCompliance(nextStiffness);

      undo.pushOnce();

      try {
        await Promise.all(
          baselinesRef.current.map((baseline) => {
            const selfCollisionThickness = baseline.isCloth
              ? selfCollisionPctToThickness(
                  nextSelfCollision,
                  baseline.meshSize,
                )
              : 0;
            return s.simulateGravity(baseline.objectId, {
              compliance,
              bendingCompliance,
              tautness: tautnessParam,
              pinGroupId: nextPinGroupId ?? undefined,
              selfCollisionThickness,
              // One-click path caps substeps for a responsive drape. The
              // solver still short-circuits early on settle-detection, so
              // this is a ceiling, not a target. Users who need the full
              // settle evolution reach for "Bake Time Range" instead.
              maxSubsteps: ENDPOINT_BAKE_MAX_SUBSTEPS,
              skipUndo: true,
              recordStride: 0,
              signal: controller.signal,
              baseline: {
                curveData: baseline.curveData,
                vertices: baseline.vertices,
              },
            });
          }),
        );
      } catch {
        // settleParticlesAsync swallows GravitySimCancelledError inside
        // simulateGravity; any error reaching here is unexpected and not
        // worth crashing the menu over.
      }
      // Stale completion guard — a newer bake may have started in the
      // meantime, in which case this one's UI updates are no longer the
      // source of truth.
      if (seq !== bakeSeqRef.current) return;
      abortRef.current = null;
      setBakePhase(null);
      setBakeProgress(undefined);
    },
    [cancelInFlight, undo],
  );

  /**
   * Trajectory bake — opt-in via the disclosure section. Solves from
   * substep 0 to `endSubstep` and records frames between
   * `[startSubstep, endSubstep]`. On completion the Time slider unlocks
   * for scrubbing.
   */
  const bakeTrajectory = useCallback(
    async (startSubstep: number, endSubstep: number) => {
      cancelInFlight();
      const controller = new AbortController();
      abortRef.current = controller;
      const seq = ++bakeSeqRef.current;

      const clampedStart = Math.max(
        0,
        Math.min(endSubstep, Math.floor(startSubstep)),
      );
      const clampedEnd = Math.max(clampedStart + 1, Math.floor(endSubstep));

      bakesRef.current.clear();
      setHasTrajectoryBake(false);
      setTime(100);
      setBakePhase("trajectory");
      setBakeProgress(0);

      const s = useStore.getState();
      const compliance = elasticityToCompliance(elasticity);
      const tautnessParam = tautnessToParam(tautness);
      const bendingCompliance = stiffnessToBendingCompliance(stiffness);

      undo.pushOnce();

      let anyBaked = false;
      try {
        await Promise.all(
          baselinesRef.current.map((baseline) => {
            if (!baseline.canBake) return Promise.resolve();
            const selfCollisionThickness = baseline.isCloth
              ? selfCollisionPctToThickness(selfCollision, baseline.meshSize)
              : 0;
            return s.simulateGravity(baseline.objectId, {
              compliance,
              bendingCompliance,
              tautness: tautnessParam,
              pinGroupId: pinGroupId ?? undefined,
              selfCollisionThickness,
              maxSubsteps: clampedEnd,
              skipUndo: true,
              recordStride: BAKE_STRIDE,
              recordStartSubstep: clampedStart,
              signal: controller.signal,
              onBakeComplete: (bake) => {
                bakesRef.current.set(bake.objectId, bake);
                anyBaked = true;
              },
              onProgress: (ev) => {
                if (seq !== bakeSeqRef.current) return;
                const denom = ev.total > 0 ? ev.total : clampedEnd;
                setBakeProgress(Math.max(0, Math.min(1, ev.substep / denom)));
              },
              baseline: {
                curveData: baseline.curveData,
                vertices: baseline.vertices,
              },
            });
          }),
        );
      } catch {
        // see rebakeEndpoint
      }
      if (seq !== bakeSeqRef.current) return;
      abortRef.current = null;
      setBakePhase(null);
      setBakeProgress(undefined);
      setHasTrajectoryBake(anyBaked);
    },
    [
      cancelInFlight,
      undo,
      elasticity,
      tautness,
      stiffness,
      selfCollision,
      pinGroupId,
    ],
  );

  /** Scrub-only: O(vertexCount) per frame, no solver. */
  const scrubTime = useCallback((nextTime: number) => {
    setTime(nextTime);
    const pct = Math.max(0, Math.min(100, nextTime)) / 100;
    const s = useStore.getState();
    for (const baseline of baselinesRef.current) {
      const bake = bakesRef.current.get(baseline.objectId);
      if (!bake || bake.empty || bake.frameCount === 0) continue;
      const frameF = pct * (bake.frameCount - 1);
      const scratch = new Float32Array(bake.vertexCount * 3);
      sampleBakeFrame(bake, frameF, scratch);
      s.applyGravityFrame(baseline.objectId, scratch);
    }
  }, []);

  // On open: collect baselines for every target the operator will drape,
  // then kick off the default endpoint bake.
  useEffect(() => {
    if (!menu.open) return;
    setElasticity(0);
    setTautness(0);
    setStiffness(50);
    setSelfCollision(0);
    setTime(100);
    setPinGroupId(null);
    setTrajectoryStart(0);
    setTrajectoryEnd(DEFAULT_END_SUBSTEP);
    setHasTrajectoryBake(false);
    setBakePhase(null);
    setBakeProgress(undefined);
    undo.reset();

    const s = useStore.getState();
    const em = s.editMode;
    const targetIds: string[] = em
      ? [em.objectId]
      : Array.from(
          selectionToLeafObjectIds(
            s.blendFiles,
            s.collections,
            s.selectedObjectTargets,
          ),
        );

    const baselines: GravityBaseline[] = [];
    let anyCloth = false;
    for (const id of targetIds) {
      const obj = s.sceneObjects.get(id);
      if (!obj) continue;
      const topology = obj.topology;
      const hasFaces = topology ? topology.offsets.length > 1 : false;
      let hasWireEdges = false;
      if (topology) {
        const flags = topology.edgeFlags;
        for (let i = 0; i < flags.length; i++) {
          if (flags[i] & EDGE_WIRE) {
            hasWireEdges = true;
            break;
          }
        }
      }
      const isCloth = hasFaces && !hasWireEdges;
      const canBake = !!topology && (hasFaces || hasWireEdges);
      if (isCloth) anyCloth = true;
      baselines.push({
        objectId: id,
        curveData: obj.curveData,
        vertices: obj.vertices,
        isCloth,
        canBake,
        meshSize: isCloth && obj.vertices ? verticesDiagonal(obj.vertices) : 0,
      });
    }
    baselinesRef.current = baselines;
    setHasClothTarget(anyCloth);

    let groupOptions: VertexGroupOption[] = [];
    let unavailableReason: string | null = null;
    const firstCurveOnly =
      baselines.length > 0 &&
      baselines.every((b) => {
        const obj = s.sceneObjects.get(b.objectId);
        return obj?.objectKind === "curve";
      });
    if (firstCurveOnly) {
      unavailableReason = "Not available for curves";
    } else {
      for (const baseline of baselines) {
        const obj = s.sceneObjects.get(baseline.objectId);
        const options = toVertexGroupOptions(obj?.vertexGroups);
        if (options.length > 0) {
          groupOptions = options;
          break;
        }
      }
      if (groupOptions.length === 0) {
        unavailableReason = "No vertex groups available";
      }
    }
    setPinGroupOptions(groupOptions);
    setPinGroupUnavailableReason(unavailableReason);

    if (baselines.length > 0) {
      rebakeEndpoint(0, 0, 50, 0, null);
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on open change
  }, [menu.open, rebakeEndpoint, undo.reset]);

  const pinGroupDisabled = pinGroupUnavailableReason !== null;
  const bakeButtonDisabled = bakePhase !== null;
  const bakeBarLabel =
    bakePhase === "trajectory" ? "Baking trajectory…" : "Settling…";

  return (
    <FloatingCommandMenu
      open={menu.open}
      onClose={close}
      title="Simulate Gravity"
      width={260}
      anchorPoint={menu.anchorPoint}
    >
      <div
        style={{
          padding: "4px 6px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_DIM,
            width: 60,
            flexShrink: 0,
          }}
        >
          Pin Group
        </span>
        <div
          style={{
            flex: 1,
            opacity: pinGroupDisabled ? 0.5 : 1,
            pointerEvents: pinGroupDisabled ? "none" : "auto",
          }}
        >
          <SelectSearch
            value={pinGroupId}
            options={pinGroupOptions}
            onChange={(v) =>
              rebakeEndpoint(elasticity, tautness, stiffness, selfCollision, v)
            }
            placeholder={pinGroupUnavailableReason ?? "Default"}
            searchPlaceholder="Search groups…"
          />
        </div>
      </div>
      <div
        style={{
          padding: "4px 6px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_DIM,
            width: 60,
            flexShrink: 0,
          }}
        >
          Elasticity
        </span>
        <Slider
          value={elasticity}
          min={0}
          max={100}
          onChange={(v) =>
            rebakeEndpoint(v, tautness, stiffness, selfCollision, pinGroupId)
          }
          dimChromeOnDrag
        />
        <span style={valueDisplayStyle}>{elasticity}%</span>
      </div>
      <div
        style={{
          padding: "4px 6px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_DIM,
            width: 60,
            flexShrink: 0,
          }}
        >
          Tautness
        </span>
        <Slider
          value={tautness}
          min={0}
          max={100}
          onChange={(v) =>
            rebakeEndpoint(elasticity, v, stiffness, selfCollision, pinGroupId)
          }
          dimChromeOnDrag
        />
        <span style={valueDisplayStyle}>{tautness}%</span>
      </div>
      {hasClothTarget && (
        <>
          <div
            style={{
              padding: "4px 6px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: FONT_SIZE_SMALL,
                color: COLOR_TEXT_DIM,
                width: 60,
                flexShrink: 0,
              }}
            >
              Stiffness
            </span>
            <Slider
              value={stiffness}
              min={0}
              max={100}
              onChange={(v) =>
                rebakeEndpoint(
                  elasticity,
                  tautness,
                  v,
                  selfCollision,
                  pinGroupId,
                )
              }
              dimChromeOnDrag
            />
            <span style={valueDisplayStyle}>{stiffness}%</span>
          </div>
          <div
            style={{
              padding: "4px 6px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: FONT_SIZE_SMALL,
                color: COLOR_TEXT_DIM,
                width: 60,
                flexShrink: 0,
              }}
            >
              Self-Coll.
            </span>
            <Slider
              value={selfCollision}
              min={0}
              max={100}
              onChange={(v) =>
                rebakeEndpoint(elasticity, tautness, stiffness, v, pinGroupId)
              }
              dimChromeOnDrag
            />
            <span style={valueDisplayStyle}>{selfCollision}%</span>
          </div>
        </>
      )}

      {hasTrajectoryBake && (
        <div
          style={{
            padding: "4px 6px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_DIM,
              width: 60,
              flexShrink: 0,
            }}
          >
            Time
          </span>
          <Slider
            value={time}
            min={0}
            max={100}
            onChange={(v) => scrubTime(v)}
            dimChromeOnDrag
          />
          <span style={valueDisplayStyle}>{time}%</span>
        </div>
      )}

      {bakePhase !== null && (
        <div
          style={{
            padding: "6px 6px 4px 6px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <ProgressBar
              value={bakePhase === "trajectory" ? bakeProgress : undefined}
              label={bakeBarLabel}
              hideCaption={false}
            />
          </div>
          <Button
            type="button"
            onClick={() => {
              cancelInFlight();
              setBakePhase(null);
              setBakeProgress(undefined);
            }}
            variant="secondary"
            size="xs"
            style={{ flexShrink: 0 }}
          >
            Cancel
          </Button>
        </div>
      )}

      <div style={{ padding: "4px 0px 0px 0px" }}>
        <FoldoutSection
          id="gravity-time-range"
          label="Bake Time Range"
          defaultCollapsed={true}
        >
          <div
            style={{
              padding: "6px 6px 4px 6px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: FONT_SIZE_SMALL,
                  color: COLOR_TEXT_DIM,
                  width: 60,
                  flexShrink: 0,
                }}
              >
                Start
              </span>
              <input
                type="number"
                min={0}
                max={trajectoryEnd}
                step={1}
                value={trajectoryStart}
                onChange={(e) => {
                  const val = Number.parseInt(e.target.value, 10);
                  if (Number.isFinite(val)) {
                    setTrajectoryStart(
                      Math.max(0, Math.min(trajectoryEnd, val)),
                    );
                  }
                }}
                style={substepInputStyle}
              />
              <span
                style={{
                  fontSize: FONT_SIZE_SMALL,
                  color: COLOR_TEXT_DIM,
                  flexShrink: 0,
                }}
              >
                substep
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: FONT_SIZE_SMALL,
                  color: COLOR_TEXT_DIM,
                  width: 60,
                  flexShrink: 0,
                }}
              >
                End
              </span>
              <input
                type="number"
                min={trajectoryStart + 1}
                step={1}
                value={trajectoryEnd}
                onChange={(e) => {
                  const val = Number.parseInt(e.target.value, 10);
                  if (Number.isFinite(val) && val > trajectoryStart) {
                    setTrajectoryEnd(val);
                  }
                }}
                style={substepInputStyle}
              />
              <span
                style={{
                  fontSize: FONT_SIZE_SMALL,
                  color: COLOR_TEXT_DIM,
                  flexShrink: 0,
                }}
              >
                substep
              </span>
            </div>
            <Button
              type="button"
              disabled={bakeButtonDisabled}
              onClick={() => bakeTrajectory(trajectoryStart, trajectoryEnd)}
              variant="secondary"
              size="xs"
              style={{
                marginTop: 4,
              }}
            >
              {bakePhase === "trajectory" ? "Baking…" : "Bake"}
            </Button>
          </div>
        </FoldoutSection>
      </div>
    </FloatingCommandMenu>
  );
}

const valueDisplayStyle = {
  fontSize: FONT_SIZE_SMALL,
  color: COLOR_TEXT_SECONDARY,
  width: 36,
  textAlign: "right" as const,
  fontVariantNumeric: "tabular-nums" as const,
  flexShrink: 0,
};

const substepInputStyle = {
  flex: 1,
  padding: "3px 6px",
  fontSize: FONT_SIZE_SMALL,
  borderRadius: RADIUS_CONTROL,
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
  background: COLOR_SURFACE,
  color: COLOR_TEXT_SECONDARY,
  width: 0,
  fontVariantNumeric: "tabular-nums" as const,
};
